import { useState, useEffect } from 'react';
import { type PageContent, type ThemeData, type ContentSection, siteMapNodes, type SiteNode } from '@/mocks/websiteBuilder';
import api from '@/services/api';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';
import { ACTIONS } from '../../../telemetry/constants/actions';
import { useCredits } from '../../../hooks/useCredits';

interface DownloadProps {
  pages: PageContent[];
  theme: ThemeData;
  brandName: string;
  brandLogo: string | null;
  globalContactInfo: GlobalContactInfo;
  onDownloaded?: () => void;
}

import { generatePageHTML } from '@/utils/htmlGenerator';




export default function DownloadOutput({ pages, theme, brandName, brandLogo, globalContactInfo, onDownloaded }: DownloadProps) {
  const [animating, setAnimating] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloaded, setDownloaded] = useState(false);
  const { trackAction } = useTelemetry({ module: MODULES.WEBSITE_BUILDER, submodule: 'Download Output' });
  const { invalidateCredits } = useCredits();

  useEffect(() => {
    setAnimating(true);
  }, []);

  const handleDownload = async () => {
    setDownloaded(false);
    setDownloadProgress(0);
    trackAction(ACTIONS.PUBLISH_WEBSITE, 'Download HTML Export');

    // 1. Start progress interval up to 90%
    const interval = setInterval(() => {
      setDownloadProgress(prev => {
        if (prev >= 90) return 90;
        return prev + 5;
      });
    }, 150);

    // 2. Trigger the actual backend download
    try {
      await performDownload();
      clearInterval(interval);
      setDownloadProgress(100);
      invalidateCredits();
      setTimeout(() => {
        setDownloaded(true);
        onDownloaded?.();
      }, 500);
    } catch (e) {
      clearInterval(interval);
      setDownloadProgress(0);
      console.error(e);
      alert("Failed to download ZIP. Make sure the Python backend is running!");
    }
  };

  const performDownload = async () => {
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

      // 1. DYNAMICALLY GENERATE THE HTML STRINGS FOR EVERY PAGE
      const htmlFiles = pages.map(page => {
        const slug = page.pageName.toLowerCase().replace(/\s+/g, '-');
        const filename = slug === 'home' ? 'index.html' : `${slug}.html`;
        
        return {
          filename: filename,
          content: generatePageHTML(page, theme, pages, brandName, brandLogo, globalContactInfo)
        };
      });

      // 1.5 DYNAMICALLY GENERATE SITEMAP AND ROBOTS.TXT
      const sitemapContent = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${pages.map(page => {
  const slug = page.pageName.toLowerCase().replace(/\s+/g, '-');
  const filename = slug === 'home' ? 'index.html' : `${slug}.html`;
  return `  <url>\n    <loc>https://yourdomain.com/${filename}</loc>\n    <changefreq>weekly</changefreq>\n    <priority>${slug === 'home' ? '1.0' : '0.8'}</priority>\n  </url>`;
}).join('\n')}
</urlset>`;

      const robotsContent = `User-agent: *
Allow: /

Sitemap: https://yourdomain.com/sitemap.xml
`;

      htmlFiles.push({ filename: 'sitemap.xml', content: sitemapContent });
      htmlFiles.push({ filename: 'robots.txt', content: robotsContent });

      // 2. SEND THE STRINGS TO THE BACKEND ZIP ENGINE
      const templateId = `Custom Design - ${theme.headingFont} Theme`;
      const blob = await api.exportWebsiteZip(templateId, htmlFiles, brandName, brandLogo);

      // 3. DOWNLOAD THE FINAL ZIP BUNDLE
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my_website_bundle.zip'; 
      document.body.appendChild(a);
      a.click();
      
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
  };

  const includedCount = pages.reduce((sum, p) => sum + p.sections.filter(s => s.enabled !== false).length, 0);
  const skippedCount = pages.reduce((sum, p) => sum + p.sections.filter(s => s.enabled === false).length, 0);

  const checklistItems = [
    { label: 'Sitemap generated', checked: true },
    { label: `${includedCount} sections included`, checked: true },
    { label: skippedCount > 0 ? `${skippedCount} sections skipped` : 'No sections skipped', checked: skippedCount === 0 },
    { label: 'Template & theme applied', checked: true },
    { label: 'HTML generated', checked: true },
    { label: 'Responsive breakpoints set', checked: true },
  ];

  return (
    <div className="w-full h-full bg-[#fdfdfc] flex flex-col items-center justify-center min-h-[calc(100vh-280px)] max-w-6xl mx-auto px-8 py-2">
      <div className="bg-white border border-[#e7edf5] rounded-2xl shadow-[0_8px_30px_rgba(15,23,42,0.06)] p-8 text-center max-w-xl w-full relative z-10">
        <div className={`mb-5 transition-all duration-700 ${animating ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}>
          <div className="w-20 h-20 mx-auto bg-gradient-to-b from-[#eef4fb] to-[#f8fbff] rounded-full flex items-center justify-center shadow-sm border border-[#dfe8f3]">
            <i className="ri-checkbox-circle-fill text-[#0859b8] text-[36px]"></i>
          </div>
        </div>

        <h1 className={`font-semibold text-2xl text-[#222222] mb-3 transition-all duration-700 delay-200 ${animating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
          Your Website is Ready
        </h1>

        <p className={`text-sm text-[#6b7280] mb-6 leading-relaxed max-w-md mx-auto transition-all duration-700 delay-300 ${animating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
          {skippedCount > 0
            ? `${includedCount} sections will be included in your download. ${skippedCount} skipped sections have been excluded.`
            : `All ${includedCount} sections will be included in your download.`}
        </p>

        <div className={`bg-[#fafcfe] rounded-2xl border border-[#edf2f7] p-6 mb-8 text-left transition-all duration-700 delay-400 ${animating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
          <h3 className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-4 text-center">Completion Checklist</h3>
          <div className="grid grid-cols-2 gap-y-3 gap-x-6">
            {checklistItems.map((item, idx) => (
              <div key={idx} className={`flex items-center gap-2.5 transition-all duration-500 ${animating ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: `${500 + idx * 80}ms` }}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item.checked ? 'bg-[#e8f9fb] border border-[#cdeff3]' : 'bg-[#fff7e8] border border-[#f6ddb0]'}`}>
                  <i className={`${item.checked ? 'ri-check-line text-[#0b7f8f]' : 'ri-alert-line text-[#b26a00]'} text-[10px]`}></i>
                </div>
                <span className="text-[13px] text-[#4c6a92] leading-snug">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`transition-all duration-700 delay-700 ${animating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
          {!downloaded ? (
            <button
              onClick={handleDownload}
              disabled={downloadProgress > 0 && downloadProgress < 100}
              className="inline-flex items-center gap-2 bg-[#0859b8] hover:bg-[#06489a] text-white font-medium px-8 py-3 rounded-xl transition-all duration-150 hover:-translate-y-[1px] hover:shadow-md cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-download-line text-lg"></i>
              Download Website HTML
            </button>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-[#0b7f8f] mb-2">
                <i className="ri-check-double-line"></i>
                <span className="text-sm font-medium">Downloaded successfully</span>
              </div>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setDownloaded(false)}
                  className="inline-flex items-center gap-2 bg-white border border-[#dfe8f3] text-[#0859b8] hover:bg-[#f5f9ff] hover:border-[#bfd7f5] text-sm font-medium px-6 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-sm"
                >
                  <i className="ri-download-line"></i>
                  Download Again
                </button>
              </div>
            </div>
          )}

          {downloadProgress > 0 && downloadProgress < 100 && (
            <div className="mt-5 max-w-xs mx-auto">
              <div className="h-2 bg-[#f1f5f9] rounded-full overflow-hidden border border-[#e7edf5]">
                <div className="h-full bg-[#0859b8] rounded-full transition-all duration-100" style={{ width: `${downloadProgress}%` }}></div>
              </div>
              <span className="text-xs text-[#6b7280] mt-2 block font-medium">{downloadProgress}% — Building HTML...</span>
            </div>
          )}
        </div>

        <div className={`mt-6 pt-5 border-t border-[#f1f5f9] flex items-center justify-center gap-4 text-xs text-[#6b7280] transition-all duration-700 delay-800 ${animating ? 'opacity-100' : 'opacity-0'}`}>
          <span className="flex items-center gap-1.5 font-medium">
            <i className="ri-pages-line text-[#a0aec0]"></i>
            {pages.filter(p => p.sections.some(s => s.enabled !== false)).length} active pages
          </span>
          <span className="w-1 h-1 bg-[#cbd5e1] rounded-full"></span>
          <span className="flex items-center gap-1.5 font-medium">
            <i className="ri-layout-masonry-line text-[#a0aec0]"></i>
            {includedCount} sections
          </span>
          <span className="w-1 h-1 bg-[#cbd5e1] rounded-full"></span>
          <span className="flex items-center gap-1.5 font-medium">
            <i className="ri-palette-line text-[#a0aec0]"></i>
            {theme.headingFont}
          </span>
        </div>
      </div>
    </div>
  );
}